import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, UserPlus, ArrowRightLeft, Trash2, Crown, Shield } from 'lucide-react';
import { api } from '../lib/api';
import type { BandMember } from '@shared/types';

interface Props {
  baseChartId: number;
  bandId: number;
  onClose: () => void;
}

export function BaseChartEditorsPanel({ baseChartId, bandId, onClose }: Props) {
  const queryClient = useQueryClient();
  const [transferTarget, setTransferTarget] = useState('');
  const [addTarget, setAddTarget] = useState('');
  const [showTransfer, setShowTransfer] = useState(false);

  const { data: editorsInfo, isLoading: editorsLoading } = useQuery({
    queryKey: ['baseChartEditors', baseChartId],
    queryFn: () => api.charts.getBaseEditors(baseChartId),
  });

  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ['bandMembers', bandId],
    queryFn: () => api.bands.getMembers(bandId),
  });

  const admins: BandMember[] = (members || []).filter((m: BandMember) => m.role === 'admin');

  const addEditor = useMutation({
    mutationFn: (userId: string) => api.charts.addBaseEditor(baseChartId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['baseChartEditors', baseChartId] });
      queryClient.invalidateQueries({ queryKey: ['baseChart', baseChartId] });
      setAddTarget('');
    },
  });

  const removeEditor = useMutation({
    mutationFn: (userId: string) => api.charts.removeBaseEditor(baseChartId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['baseChartEditors', baseChartId] });
      queryClient.invalidateQueries({ queryKey: ['baseChart', baseChartId] });
    },
  });

  const transferOwnership = useMutation({
    mutationFn: (newOwnerUserId: string) => api.charts.transferBaseOwnership(baseChartId, newOwnerUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['baseChartEditors', baseChartId] });
      queryClient.invalidateQueries({ queryKey: ['baseChart', baseChartId] });
      setShowTransfer(false);
      setTransferTarget('');
    },
  });

  const currentEditorIds = new Set((editorsInfo?.editors || []).map((e: any) => e.userId));
  const ownerUserId = editorsInfo?.owner?.userId;

  const eligibleForEditor = admins.filter(
    (m: BandMember) => m.userId !== ownerUserId && !currentEditorIds.has(m.userId)
  );
  const eligibleForTransfer = admins.filter(
    (m: BandMember) => m.userId !== ownerUserId
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-surface-900 border border-surface-700 rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-700 shrink-0">
          <h2 className="text-base font-semibold text-white">Manage Editors</h2>
          <button onClick={onClose} className="p-1 hover:bg-surface-800 rounded transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-6">
          {editorsLoading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : (
            <>
              {/* Owner section */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Owner</p>
                <div className="flex items-center justify-between bg-surface-800 rounded-lg px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <Crown className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                    <span className="text-sm text-white">{editorsInfo?.owner?.name || '—'}</span>
                  </div>
                  <button
                    onClick={() => setShowTransfer(!showTransfer)}
                    className="text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1 transition-colors"
                  >
                    <ArrowRightLeft className="w-3 h-3" />
                    Transfer
                  </button>
                </div>

                {showTransfer && (
                  <div className="mt-2 bg-surface-800/60 rounded-lg p-3 space-y-2">
                    <p className="text-xs text-gray-400">Transfer ownership to an admin:</p>
                    {membersLoading ? (
                      <p className="text-xs text-gray-500">Loading admins…</p>
                    ) : eligibleForTransfer.length === 0 ? (
                      <p className="text-xs text-gray-500">No other admins available.</p>
                    ) : (
                      <>
                        <select
                          value={transferTarget}
                          onChange={e => setTransferTarget(e.target.value)}
                          className="w-full bg-surface-700 border border-surface-600 rounded text-sm text-white px-2 py-1.5"
                        >
                          <option value="">Select admin…</option>
                          {eligibleForTransfer.map((m: BandMember) => (
                            <option key={m.userId} value={m.userId}>
                              {m.user?.displayName || m.userId}
                            </option>
                          ))}
                        </select>
                        <button
                          disabled={!transferTarget || transferOwnership.isPending}
                          onClick={() => transferOwnership.mutate(transferTarget)}
                          className="w-full bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-xs font-medium py-1.5 px-3 rounded transition-colors"
                        >
                          {transferOwnership.isPending ? 'Transferring…' : 'Confirm Transfer'}
                        </button>
                        {transferOwnership.isError && (
                          <p className="text-xs text-red-400">{(transferOwnership.error as any)?.message || 'Transfer failed'}</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Editors section */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Assigned Editors</p>
                {editorsInfo?.editors?.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No editors assigned.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {editorsInfo?.editors?.map((editor: any) => (
                      <li key={editor.userId} className="flex items-center justify-between bg-surface-800 rounded-lg px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <Shield className="w-3.5 h-3.5 text-primary-400 shrink-0" />
                          <span className="text-sm text-white">{editor.name}</span>
                        </div>
                        <button
                          onClick={() => removeEditor.mutate(editor.userId)}
                          disabled={removeEditor.isPending}
                          className="p-1 hover:bg-red-600/20 rounded transition-colors text-gray-500 hover:text-red-400"
                          title="Remove editor"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Add editor */}
                <div className="mt-3 flex gap-2">
                  {membersLoading ? null : eligibleForEditor.length === 0 ? (
                    <p className="text-xs text-gray-500 italic">All admins are already editors or owners.</p>
                  ) : (
                    <>
                      <select
                        value={addTarget}
                        onChange={e => setAddTarget(e.target.value)}
                        className="flex-1 bg-surface-700 border border-surface-600 rounded text-sm text-white px-2 py-1.5"
                      >
                        <option value="">Add admin as editor…</option>
                        {eligibleForEditor.map((m: BandMember) => (
                          <option key={m.userId} value={m.userId}>
                            {m.user?.displayName || m.userId}
                          </option>
                        ))}
                      </select>
                      <button
                        disabled={!addTarget || addEditor.isPending}
                        onClick={() => addEditor.mutate(addTarget)}
                        className="flex items-center gap-1 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white text-xs font-medium py-1.5 px-3 rounded transition-colors shrink-0"
                      >
                        <UserPlus className="w-3.5 h-3.5" />
                        Add
                      </button>
                    </>
                  )}
                </div>
                {addEditor.isError && (
                  <p className="text-xs text-red-400 mt-1">{(addEditor.error as any)?.message || 'Failed to add editor'}</p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-surface-700 shrink-0">
          <button
            onClick={onClose}
            className="w-full bg-surface-700 hover:bg-surface-600 text-white text-sm font-medium py-2 rounded-lg transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
